<script lang="ts">
  /**
   * 검증 배지 — 두 종은 서로 다른 사실이다. 절대 합치지 않는다.
   *
   *   platform : 운영진이 인정한 인물 (users.platform_verified_at, 관리자 수동 승인)
   *   company  : 소속 확인          (users.company_verification_status == "verified")
   *
   * 🔴 users.verified_at 은 **이메일 인증** 시각이다. 배지로 쓰면 가입만 하면
   *    누구나 검증된 인물이 된다 — 서버가 아예 내려주지 않는다.
   *
   * variant (2026-08-17 시안 C 확정)
   *   icon : 목록·작성자 라인 — 밀도 우선
   *   chip : 프로필 — 무슨 검증인지 읽혀야 하는 곳
   */
  type Kind = 'platform' | 'company';

  let {
    kind,
    variant = 'icon'
  }: { kind: Kind; variant?: 'icon' | 'chip' } = $props();

  const LABEL: Record<Kind, string> = {
    platform: '검증',
    company: '소속확인'
  };
  const TITLE: Record<Kind, string> = {
    platform: '운영진이 확인한 인물입니다',
    company: '소속이 확인된 계정입니다'
  };
  // 🔴 아이콘 정본은 `icons/badge-{platform,company}.svg` 다. 아래 path 는 그
  //    파일에서 옮긴 것이고, `spec/integration/badge_asset_parity_spec.rb` 가
  //    정본에서 읽어 이 문자열과 대조한다 — 손으로 고치면 그 스펙이 먼저 깨진다.
  //
  // 🔴 체크·손잡이는 흰색으로 덮은 것이 아니라 `fill-rule="evenodd"` 로 뚫은
  //    구멍이다. 그래야 칩 배경이 그대로 비치고, 다크 모드에서 흰 조각이 뜨지 않는다.
  //    색은 `currentColor` 하나뿐 — 아래 텍스트 색 클래스가 그대로 아이콘 색이 된다.
  const PATH: Record<Kind, string> = {
    platform:
      'M12 1.6l2.6 2.1 3.3-.4 1 3.2 2.9 1.7-1.3 3.1 1.3 3.1-2.9 1.7-1 3.2-3.3-.4L12 22.4l-2.6-2.1-3.3.4-1-3.2L2.2 15.8l1.3-3.1-1.3-3.1L5.1 7.9l1-3.2 3.3.4L12 1.6zM10.8 15.4l-3-3 1.3-1.3 1.7 1.7 4.1-4.1 1.3 1.3-5.4 5.4z',
    company:
      'M9 4h6a1 1 0 0 1 1 1v2h4a1 1 0 0 1 1 1v11a1 1 0 0 1 -1 1H4a1 1 0 0 1 -1 -1V8a1 1 0 0 1 1 -1h4V5a1 1 0 0 1 1 -1zm1 3h4V6h-4v1z'
  };
</script>

{#if variant === 'chip'}
  <span
    class="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold
           {kind === 'platform'
             ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
             : 'bg-success-light text-[#00A846] dark:bg-[#0E2A18] dark:text-[#4ADE80]'}"
    title={TITLE[kind]}
  >
    <svg
      class="h-3 w-3 shrink-0"
      viewBox="0 0 24 24"
      fill="currentColor"
      fill-rule="evenodd"
      aria-hidden="true"
    >
      <path d={PATH[kind]} />
    </svg>{LABEL[kind]}
  </span>
{:else}
  <span
    class="inline-flex leading-none
           {kind === 'platform' ? 'text-primary' : 'text-[#00A846] dark:text-[#4ADE80]'}"
    title={TITLE[kind]}
  >
    <svg
      class="h-[0.9375rem] w-[0.9375rem] shrink-0"
      viewBox="0 0 24 24"
      fill="currentColor"
      fill-rule="evenodd"
      aria-hidden="true"
    >
      <path d={PATH[kind]} />
    </svg>
    <span class="sr-only">{LABEL[kind]}</span>
  </span>
{/if}
